/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
export const AdminContext = createContext();
import { toast } from "react-toastify";
import { generateInvoicePDF } from "../utils/pdfGenerator";
import { canAccessModule } from "../utils/adminAccess";

const normalizeContract = (contract) => {
  if (!contract) return contract;

  return {
    ...contract,
    client: contract.client || contract.user || null,
    platformPercentage:
      contract.platformPercentage ?? contract.platformPct ?? 20,
    terminationReason:
      contract.terminationReason ?? contract.terminationNote ?? "",
    payouts: (contract.payouts || []).map((payout) => ({
      ...payout,
      month: payout.month ?? payout.periodStart,
      paidAt: payout.paidAt ?? payout.settledAt,
    })),
  };
};

const normalizeDashboardData = (dashData) => {
  if (!dashData) return dashData;

  const totalCaregivers = dashData.totalCaregivers ?? dashData.careGivers ?? 0;
  const onboardedCaregivers =
    dashData.onboardedCaregivers ??
    dashData.caregiverFunnel?.onboardedCaregivers ??
    0;
  const totalApplications =
    dashData.jobMetrics?.totalApplications ??
    dashData.caregiverFunnel?.totalApplications ??
    0;
  const jobsApplied =
    dashData.caregiverFunnel?.jobsApplied ?? totalApplications;

  const latestBookings = (
    dashData.latestBookings ||
    dashData.latestAppointments ||
    []
  ).map((item) => ({
    ...item,
    _id: item.id,
    careGiverData: item.careGiverData ||
      item.caregiver || { name: "Unknown", image: "" },
    userData: item.userData || item.user || { name: "N/A" },
    category: item.category || item.serviceCategory?.label || "General",
  }));

  const categoryStats = (dashData.categoryStats || []).map((stat) => ({
    ...stat,
    category: stat.category || stat.label || "Uncategorized",
    caregivers: stat.caregivers ?? stat._count?.caregivers ?? 0,
  }));

  return {
    ...dashData,
    totalCaregivers,
    caregiverFunnel: {
      ...(dashData.caregiverFunnel || {}),
      totalCaregivers,
      onboardedCaregivers,
      totalApplications,
      jobsApplied,
    },
    appointmentFunnel: dashData.appointmentFunnel || {
      totalAppointments: dashData.bookingFunnel?.totalBookings || 0,
      completed: dashData.bookingFunnel?.completedBookings || 0,
      workStarted: dashData.bookingFunnel?.workStartedBookings || 0,
      cancelled: dashData.bookingFunnel?.cancelledBookings || 0,
    },
    bookingFunnel: dashData.bookingFunnel || {
      totalBookings:
        dashData.bookingFunnel?.totalBookings ||
        dashData.appointmentFunnel?.totalAppointments ||
        0,
      completed:
        dashData.bookingFunnel?.completedBookings ||
        dashData.appointmentFunnel?.completed ||
        0,
      workStarted:
        dashData.bookingFunnel?.workStartedBookings ||
        dashData.appointmentFunnel?.workStarted ||
        0,
      cancelled:
        dashData.bookingFunnel?.cancelledBookings ||
        dashData.appointmentFunnel?.cancelled ||
        0,
    },
    serviceRequestMetrics: dashData.serviceRequestMetrics ||
      dashData.jobMetrics || {
        openJobs: 0,
        closedJobs: 0,
      },
    latestAppointments: latestBookings,
    latestBookings,
    categoryStats,
  };
};

const AdminContextProvider = (props) => {
  const navigate = useNavigate();
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  /* ---------------- AUTH ---------------- */
  const [aToken, setAToken] = useState(localStorage.getItem("aToken") || "");
  const [adminProfile, setAdminProfile] = useState(null);
  const [adminId, setAdminId] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => localStorage.getItem("role") === "super_admin");
  const [adminAccess, setAdminAccess] = useState(() => {
    try {
      const stored = localStorage.getItem("adminAccess");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [adminDepartment, setAdminDepartment] = useState(() => localStorage.getItem("adminDepartment") || "custom");

  /* ---------------- DATA ---------------- */
  const [careGivers, setCareGivers] = useState([]);
  const [careGiverPagination, setCareGiverPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 1,
  });
  const [vettingBoardCaregivers, setVettingBoardCaregivers] = useState([]);
  const [vettingTableCaregivers, setVettingTableCaregivers] = useState([]);
  const [vettingPagination, setVettingPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 1,
  });
  const [verifiedCareGivers, setVerifiedCareGivers] = useState([]);
  const [verifiedCareGiversPagination, setVerifiedCareGiversPagination] =
    useState({
      total: 0,
      page: 1,
      limit: 5,
      totalPages: 1,
    });

  const [bookings, setBookings] = useState([]);
  const [bookingsPagination, setBookingsPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [jobs, setJobs] = useState([]);
  const [jobsPagination, setJobsPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [payments, setPayments] = useState([]);
  const [manualPayments, setManualPayments] = useState([]);
  const [manualPaymentsPagination, setManualPaymentsPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [clients, setClients] = useState([]);
  const [clientsPagination, setClientsPagination] = useState({
    total: 0,
    page: 1,
    limit: 5,
    totalPages: 1,
  });
  const [subAdmins, setSubAdmins] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [bookPurchases, setBookPurchases] = useState([]);
  const [coverageData, setCoverageData] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [contactPagination, setContactPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5,
  });

  const [newsletters, setNewsletters] = useState([]);
  const [matchedCareGivers, setMatchedCareGivers] = useState([]); // State to store matched caregivers

  /* ---------------- PARENT HUB (BLOG) ---------------- */
  const [blogs, setBlogs] = useState([]);
  const [blogPagination, setBlogPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [blogCategories, setBlogCategories] = useState([]);
  const [blogAnalytics, setBlogAnalytics] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogsPagination, setActivityLogsPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });
  const [dashData, setDashData] = useState(false);

  /* ---------------- SERVICE CATEGORIES (Dynamic EAV) ---------------- */
  const [serviceCategories, setServiceCategories] = useState([]);

  /* ---------------- CONTRACTS & PAYOUTS ---------------- */
  const [contracts, setContracts] = useState([]);
  const [contractsPagination, setContractsPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 5,
  });
  const [payouts, setPayouts] = useState([]);
  const [wagePayments, setWagePayments] = useState([]);

  /* ---------------- REVIEWS ---------------- */
  const [reviews, setReviews] = useState([]);
  const [reviewsPagination, setReviewsPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  });

  /* ---------------- MATCHING CONFIG ---------------- */
  const [matchingConfig, setMatchingConfig] = useState({
    experienceLevel: true,
    subcity: true,
    languages: true,
    workTime: true,
    age_groups: true,
    service_frequency: true,
    preferred_work_hours: true,
    room_numbers: true,
    preferred_days: true,
    teaching_levels: true,
    hours_per_day: true,
    elderly_care_experience: true,
    first_aid_cpr_certified: true,
    transportation_assistance: true,
    meal_preparation: true,
    light_housekeeping: true,
    medication_management: true,
    mobility_assistance: true,
    companionship: true,
  });
  const [tempConfig, setTempConfig] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // const [aToken, setAToken] = useState(
  //   localStorage.getItem("aToken") ? localStorage.getItem("aToken") : ""
  // );

  /* ---------------- TOKEN PERSISTENCE ---------------- */
  useEffect(() => {
    if (aToken) {
      localStorage.setItem("aToken", aToken);
      fetchServiceCategories();
    } else {
      localStorage.removeItem("aToken");
      localStorage.removeItem("role");
      localStorage.removeItem("adminAccess");
      localStorage.removeItem("adminDepartment");
      setAdminProfile(null);
      setAdminId("");
      setIsSuperAdmin(false);
      setAdminAccess([]);
      setAdminDepartment("custom");
      setServiceCategories([]);
    }
  }, [aToken]);

  // Global axios response interceptor: on 401, clear token and redirect to login
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";

        // Only redirect to admin login if it's an admin-related request
        if (status === 401 && url.includes("/api/admin/")) {
          try {
            toast.error("Session expired. Please log in again.");
          } catch (e) {
            // ignore toast failures
          }
          setAToken("");
          localStorage.removeItem("aToken");
          navigate("/", { replace: true });
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  /* ---------------- TOKEN DECODE ---------------- */
  useEffect(() => {
    if (aToken) {
      try {
        // Split token and decode payload on JWT format)
        const payload = aToken.split(".")[1];
        if (!payload) {
          console.error("Invalid token: No payload found");
          return;
        }
        const decoded = JSON.parse(atob(payload));
        // Use 'id' as per decoded token payload
        const id = decoded.id || "";
        if (!id) {
          console.error("No admin ID found in token payload");
        }
        setAdminId(id);
      } catch (error) {
        console.error("Token decoding error:", error.message);
      }
    }
  }, [aToken]);

  // Fetch all admins
  const fetchSubAdmins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/admin/sub`, {
        headers: { aToken },
      });
      if (data.success) {
        setSubAdmins(data.admins);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch sub-admins.");
    }
  }, [aToken, backendUrl]);

  // Fetch admin profile
  const fetchAdminProfile = useCallback(async () => {
    if (!aToken) return;
    try {
      const { data } = await axios.get(`${backendUrl}/api/admin/profile`, {
        headers: { aToken },
      });

      if (data.success) {
        setAdminProfile(data.profile);
        setIsSuperAdmin(data.profile.isSuperAdmin);
        setAdminAccess(data.profile.adminAccess || []);
        setAdminDepartment(data.profile.adminDepartment || "custom");
        localStorage.setItem("role", data.profile.isSuperAdmin ? "super_admin" : "sub_admin");
        localStorage.setItem("adminAccess", JSON.stringify(data.profile.adminAccess || []));
        localStorage.setItem("adminDepartment", data.profile.adminDepartment || "custom");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch profile.");
    }
  }, [aToken, backendUrl]);

  // Automatically fetch admin profile when token exists
  useEffect(() => {
    if (aToken) {
      fetchAdminProfile();
    }
  }, [aToken, fetchAdminProfile]);

  // Fetch all service categories (dynamic EAV)
  const fetchServiceCategories = useCallback(async () => {
    if (!aToken) return;
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/service-categories`,
        {
          headers: {
            aToken,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          params: { _t: Date.now() },
        },
      );
      if (data.success) {
        setServiceCategories(data.data || data.categories || []);
      }
    } catch (error) {
      console.error("fetchServiceCategories error:", error);
    }
  }, [aToken, backendUrl]);

  const fetchServiceCategoryById = useCallback(
    async (categoryId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/service-categories/${categoryId}`,
          { headers: { aToken } },
        );
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch category",
        );
      }
    },
    [aToken, backendUrl],
  );

  const createServiceCategory = useCallback(
    async (payload) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/service-categories`,
          payload,
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Category created!");
          await fetchServiceCategories();
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create category",
        );
      }
    },
    [aToken, backendUrl, fetchServiceCategories],
  );

  const updateServiceCategory = useCallback(
    async (categoryId, payload) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/service-categories/${categoryId}`,
          payload,
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Category updated!");
          await fetchServiceCategories();
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to update category",
        );
      }
    },
    [aToken, backendUrl, fetchServiceCategories],
  );

  const toggleServiceCategoryActive = useCallback(
    async (categoryId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/service-categories/${categoryId}/toggle`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Category status updated");
          await fetchServiceCategories();
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to toggle category",
        );
      }
    },
    [aToken, backendUrl, fetchServiceCategories],
  );

  const deleteServiceCategory = useCallback(
    async (categoryId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/service-categories/${categoryId}`,
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Category deleted successfully");
          await fetchServiceCategories();
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to delete category",
        );
      }
    },
    [aToken, backendUrl, fetchServiceCategories],
  );

  const reorderServiceCategories = useCallback(
    async (reorderedItems) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/service-categories/reorder`,
          { reorderedItems },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Categories reordered successfully");
          await fetchServiceCategories();
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to reorder categories",
        );
      }
    },
    [aToken, backendUrl, fetchServiceCategories],
  );

  // Fetch service requests with filters
  const fetchServiceRequests = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/jobs`, {
          headers: { aToken },
          params: filters,
        });

        if (data.success) {
          return data.jobs || [];
        } else {
          toast.error(data.message || "Failed to fetch service requests");
          return [];
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch service requests",
        );
        return [];
      }
    },
    [aToken, backendUrl],
  );

  // Create a new service request
  const createServiceRequest = useCallback(
    async (requestData) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/post`,
          requestData,
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message || "Service request created successfully");
          return { success: true, job: data.job };
        } else {
          toast.error(data.message || "Failed to create service request");
          return { success: false, message: data.message };
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to create service request";
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    },
    [aToken, backendUrl],
  );

  // Fetch category fields for a specific service category
  const fetchCategoryFields = useCallback(
    async (categoryId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/service-categories/${categoryId}`,
          { headers: { aToken } },
        );

        if (data.success) {
          return data.data?.fields || data.category?.fields || [];
        } else {
          toast.error(data.message || "Failed to fetch category fields");
          return [];
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch category fields",
        );
        return [];
      }
    },
    [aToken, backendUrl],
  );

  // Create a sub-admin
  const createSubAdmin = useCallback(
    async (name, email, password, access = [], department = "custom") => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/create`,
          {
            name,
            email,
            password,
            isSuperAdmin: false,
            adminAccess: access,
            adminDepartment: department,
          }, // Ensure isSuperAdmin is false for sub-admins
          {
            headers: { aToken },
          },
        );

        if (data.success) {
          toast.success("Sub-admin created successfully!");
          fetchSubAdmins();
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to create sub-admin.");
      }
    },
    [aToken, backendUrl, fetchSubAdmins],
  );

  // Update admin profile
  const updateAdminProfile = useCallback(
    async (
      adminId,
      name = null,
      email = null,
      phoneNumber = null,
      address = null,
      gender = null,
      dob = null,
      imageFile = null,
      oldPassword = null,
      newPassword = null,
      confirmPassword = null,
    ) => {
      try {
        // Use FormData if there's an image file
        const isFormData = imageFile instanceof File;
        let requestData;

        if (isFormData) {
          requestData = new FormData();
          requestData.append("adminId", adminId);
          if (name !== null) requestData.append("name", name);
          if (email !== null) requestData.append("email", email);
          if (phoneNumber !== null)
            requestData.append("phoneNumber", phoneNumber);
          if (address !== null) {
            requestData.append("address[line1]", address.line1 || "");
            requestData.append("address[line2]", address.line2 || "");
          }
          if (gender !== null) requestData.append("gender", gender);
          if (dob !== null) requestData.append("dob", dob);
          requestData.append("image", imageFile);

          // password fields if provided
          if (
            oldPassword !== null &&
            newPassword !== null &&
            confirmPassword !== null
          ) {
            requestData.append("oldPassword", oldPassword);
            requestData.append("newPassword", newPassword);
            requestData.append("confirmPassword", confirmPassword);
          }
        } else {
          requestData = { adminId };

          // profile fields if they are provided
          if (name !== null) requestData.name = name;
          if (email !== null) requestData.email = email;
          if (phoneNumber !== null) requestData.phoneNumber = phoneNumber;
          if (address !== null) requestData.address = address;
          if (gender !== null) requestData.gender = gender;
          if (dob !== null) requestData.dob = dob;

          // password fields if they are provided
          if (
            oldPassword !== null &&
            newPassword !== null &&
            confirmPassword !== null
          ) {
            requestData.oldPassword = oldPassword;
            requestData.newPassword = newPassword;
            requestData.confirmPassword = confirmPassword;
          }
        }

        const { data } = await axios.put(
          `${backendUrl}/api/admin/update`,
          requestData,
          {
            headers: {
              aToken,
              ...(isFormData ? {} : { "Content-Type": "application/json" }),
            },
          },
        );

        if (data.success) {
          toast.success("Profile updated successfully!");
          // Update profile state immediately with returned data
          if (data.profile) {
            setAdminProfile(data.profile);
          }
          // Also fetch to ensure we have the latest data
          fetchAdminProfile();
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to update profile.");
      }
    },
    [aToken, backendUrl, fetchAdminProfile],
  );
  // Delete a sub-admin
  const deleteSubAdmin = useCallback(
    async (adminId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/delete/${adminId}`,
          {
            headers: { aToken },
          },
        );
        if (data.success) {
          toast.success("Sub-admin deleted successfully!");
          fetchSubAdmins();
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete sub-admin.");
      }
    },
    [aToken, backendUrl, fetchSubAdmins],
  );

  // Update sub-admin status
  const updateSubAdminStatus = useCallback(
    async (adminId, status, access = null, department = null) => {
      try {
        const payload = { adminId };
        if (status !== null && status !== undefined) payload.status = status;
        if (access !== null) payload.adminAccess = access;
        if (department !== null) payload.adminDepartment = department;

        const { data } = await axios.put(
          `${backendUrl}/api/admin/update-subadmin`,
          payload,
          {
            headers: { aToken },
          },
        );

        if (data.success) {
          toast.success("Sub-admin status updated successfully!");
          fetchSubAdmins(); // Refresh the sub-admin list
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to update sub-admin status.");
      }
    },
    [aToken, backendUrl, fetchSubAdmins],
  );

  //Get care givers
  const getAllCareGivers = useCallback(
    async ({
      page = 1,
      limit = 5,
      name = "",
      phone = "",
      category = "All",
      vettingStatus = "All",
      subcity = "",
    }) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/caregivers`, {
          params: {
            page,
            limit,
            name,
            phone,
            category,
            serviceCategoryId: category && category !== "All" ? category : "",
            vettingStatus,
            subcity,
          },
          headers: { aToken },
        });

        if (data.success) {
          setCareGivers(data.caregivers || data.data || []);
          setCareGiverPagination(
            data.pagination || {
              page: data.page || 1,
              limit: parseInt(limit),
              total: data.total || 0,
              totalPages: data.pages || 1,
            },
          );
        } else {
          toast.error(data.message || "Failed to fetch caregivers");
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [aToken, backendUrl],
  );

  // reset password for caregivers by admin
  const resetCareGiverPassword = useCallback(
    async (caregiverId) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/caregivers/${caregiverId}/reset-password`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Password reset successfully");
          return true;
        } else {
          toast.error(data.message);
          return false;
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Failed to reset caregiver password",
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  const getVettingBoard = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/vetting/board`,
          {
            params: filters,
            headers: { aToken },
          },
        );

        if (data.success) {
          // Backend now returns flat array in data.caregivers
          const caregivers = data.caregivers || data.data || [];
          setVettingBoardCaregivers(
            Array.isArray(caregivers) ? caregivers : [],
          );
        }
      } catch (error) {
        console.error("getVettingBoard error:", error);
        toast.error(error.message || "Failed to load vetting board");
      }
    },
    [aToken, backendUrl],
  );

  const getVettingTable = useCallback(
    async ({
      page = 1,
      limit = 10,
      name = "",
      phone = "",
      category = "All",
      vettingStatus = "All",
    }) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/vetting/table`,
          {
            params: { page, limit, name, phone, category, vettingStatus },
            headers: { aToken },
          },
        );

        if (data.success) {
          setVettingTableCaregivers(data.caregivers);
          setVettingPagination(data.pagination);
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [aToken, backendUrl],
  );
  const getCaregiverVettingHistory = useCallback(
    async (caregiverId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/${caregiverId}/vetting-history`,
          { headers: { aToken } },
        );

        if (data.success) {
          return data.vettingHistory;
        } else {
          toast.error(data.message || "Failed to fetch vetting history");
          return [];
        }
      } catch (error) {
        toast.error(error.message);
        return [];
      }
    },
    [aToken, backendUrl],
  );

  // FETCH COVERAGE DATA
  const fetchCoverageData = useCallback(
    async (params = {}) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/coverage`,
          {
            headers: { aToken },
            params,
          },
        );

        if (data.success) {
          setCoverageData(data.data);
        } else {
          toast.error("Failed to load coverage data");
        }
      } catch (error) {
        toast.error("Coverage fetch failed");
      }
    },
    [backendUrl, aToken],
  );
  // Fetch caregiver details by ID
  const fetchCaregiverById = useCallback(
    async (caregiverId) => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          {
            headers: {
              aToken,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
            params: { _t: Date.now() },
          },
        );
        if (response.data.success) {
          const caregiver =
            response.data.data || response.data.caregiver || null;
          if (!caregiver) return null;

          return {
            ...caregiver,
            serviceCategoryId:
              caregiver.serviceCategoryId ||
              caregiver.serviceCategory?.id ||
              caregiver.profileSnapshot?.serviceCategoryId ||
              "",
          };
        } else {
          toast.error("Failed to fetch caregiver details");
          return null;
        }
      } catch (error) {
        toast.error("Error fetching caregiver details");
        return null;
      }
    },
    [aToken, backendUrl],
  );

  // Update caregiver details by ID
  const updateCaregiverById = useCallback(
    async (caregiverId, updatedData) => {
      try {
        const response = await axios.put(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          updatedData,
          { headers: { aToken } },
        );
        if (response.data.success) {
          toast.success("Caregiver updated successfully");
          getAllCareGivers();
          return response.data;
        } else {
          toast.error("Failed to update caregiver");
          return false;
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Error updating caregiver",
        );
        return false;
      }
    },
    [aToken, backendUrl, getAllCareGivers],
  );

  // Create a new caregiver
  const createCaregiver = useCallback(
    async (caregiverData) => {
      try {
        const response = await axios.post(
          `${backendUrl}/api/admin/caregivers`,
          caregiverData,
          { headers: { aToken } },
        );
        if (response.data.success) {
          toast.success(
            response.data.message || "Caregiver created successfully",
          );
          getAllCareGivers({});
          return { success: true, data: response.data };
        } else {
          toast.error(response.data.message || "Failed to create caregiver");
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error creating caregiver";
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    },
    [aToken, backendUrl, getAllCareGivers],
  );

  // Update caregiver for edit mode (uses different endpoint)
  const updateCaregiverForEdit = useCallback(
    async (caregiverId, caregiverData) => {
      try {
        const response = await axios.put(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          caregiverData,
          { headers: { aToken } },
        );
        if (response.data.success) {
          toast.success(
            response.data.message || "Caregiver updated successfully",
          );
          getAllCareGivers({});
          return { success: true, data: response.data };
        } else {
          toast.error(response.data.message || "Failed to update caregiver");
          return { success: false, message: response.data.message };
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error updating caregiver";
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    },
    [aToken, backendUrl, getAllCareGivers],
  );

  // delete a caregiver by ID
  const deleteCareGiverById = useCallback(
    async (caregiverId) => {
      try {
        const response = await axios.delete(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          {
            headers: { aToken },
          },
        );
        if (response.data.success) {
          return {
            success: true,
            message: response.data.message || "Caregiver deleted successfully",
          };
        } else {
          return {
            success: false,
            message: response.data.message || "Failed to delete caregiver",
          };
        }
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || "Error deleting caregiver",
        };
      }
    },
    [aToken, backendUrl],
  );
  // Change Availability of CareGiver
  const changeAvailablity = useCallback(
    async (caregiverId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/caregivers/${caregiverId}/toggle-availability`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Availability updated");
          getAllCareGivers({});
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      }
    },
    [aToken, backendUrl, getAllCareGivers],
  );
  // Change Availability of Care giver (Maps to standard toggle availability now)
  const changeBookingAvailablity = useCallback(
    async (caregiverId) => {
      return changeAvailablity(caregiverId);
    },
    [changeAvailablity],
  );

  // Getting all bookings
  const getAllBookings = useCallback(
    async ({ page = 1, limit = 8, status = "All", search = "" } = {}) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/bookings`, {
          params: { page, limit, status, search },
          headers: { aToken },
        });

        if (data.success) {
          setBookings(data.data || []);
          setBookingsPagination({
            page: data.page || 1,
            limit,
            total: data.total || 0,
            totalPages: data.pages || 1,
          });
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
        console.error(error);
      }
    },
    [aToken, backendUrl],
  );
  const getBookingHistory = useCallback(
    async (bookingId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/bookings/${bookingId}/history`,
          { headers: { aToken } },
        );

        if (data.success) {
          return data.history;
        } else {
          toast.error(data.message);
          return [];
        }
      } catch (error) {
        toast.error(error.message);
        return [];
      }
    },
    [aToken, backendUrl],
  );

  // cancel booking
  const cancelBooking = useCallback(
    async (bookingId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/bookings/${bookingId}/cancel`,
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message || "Booking Cancelled");
          // Don't auto-refresh here - let the calling component handle it
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
        console.log(error);
      }
    },
    [aToken, backendUrl],
  );
  // Update booking status
  const updateBookingStatus = useCallback(
    async (bookingId, status, reminder = "") => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/bookings/${bookingId}/status`,
          { status, reminder },
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message);
          // Don't auto-refresh here - let the calling component handle it
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to update booking status";
        toast.error(errorMessage);
        console.log(error);
      }
    },
    [aToken, backendUrl],
  );

  const getCaregiversByCategory = useCallback(
    async (serviceCategoryId) => {
      try {
        if (!serviceCategoryId) {
          toast.error("Service category ID is required");
          return [];
        }

        const { data } = await axios.get(
          `${backendUrl}/api/admin/bookings/caregivers-by-category?serviceCategoryId=${serviceCategoryId}`,
          { headers: { aToken } },
        );

        return data.success ? data.data : [];
      } catch (error) {
        console.error("getCaregiversByCategory error:", error);
        toast.error("Failed to load caregivers");
        return [];
      }
    },
    [aToken, backendUrl],
  );

  const replaceCaregiver = useCallback(
    async (bookingId, newCaregiverId, reason) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/bookings/${bookingId}/replace-caregiver`,
          {
            newCaregiverId,
            reason,
          },
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message);
          // Don't auto-refresh here - let the calling component handle it
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error("replaceCaregiver error:", error);
        const errorMessage =
          error.response?.data?.message || "Replacement failed";
        toast.error(errorMessage);
      }
    },
    [aToken, backendUrl],
  );

  // Check if client has active jobs in a specific category
  const checkClientActiveJobs = useCallback(
    async (userId, category) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/clients/${userId}/active-jobs`,
          {
            params: { category },
            headers: { aToken },
          },
        );

        return data.success ? data.hasActiveJob : false;
      } catch (error) {
        console.error("checkClientActiveJobs error:", error);
        return false;
      }
    },
    [aToken, backendUrl],
  );

  //Admin Dashboard data
  const getDashData = useCallback(
    async ({
      period = null, // "week" | "month" | "year"
      start = null, // "YYYY-MM-DD"
      end = null, // "YYYY-MM-DD"
    } = {}) => {
      try {
        const queryParams = new URLSearchParams();

        // preset period (week, month, year)
        if (period) {
          queryParams.append("period", period);
        }

        // custom date range
        if (start && end) {
          queryParams.append("start", start);
          queryParams.append("end", end);
        }

        const { data } = await axios.get(
          `${backendUrl}/api/admin/dashboard/all?${queryParams.toString()}`,
          {
            headers: { aToken },
          },
        );

        if (data.success) {
          setDashData(normalizeDashboardData(data.dashData));
        } else {
          toast.error(data.message || "Failed to load dashboard");
        }
      } catch (error) {
        console.error("Dashboard fetch error:", error);
        toast.error(error.response?.data?.message || error.message);
      }
    },
    [aToken, backendUrl],
  );

  // Get all clients
  const getAllClients = useCallback(
    async ({
      page = 1,
      limit = 5,
      search = "",
      status = "All",
      gender = "all",
    }) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/clients/all`,
          {
            params: {
              page,
              limit,
              search,
              status,
              gender,
            },
            headers: { aToken },
          },
        );

        if (data.success) {
          setClients(data.clients);
          setClientsPagination(data.pagination);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [aToken, backendUrl],
  );

  // Create a client from the admin dashboard
  const createClient = useCallback(
    async (clientData) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/clients`,
          clientData,
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message || "Client created successfully");
        } else {
          toast.error(data.message || "Failed to create client");
        }
        return data;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to create client";
        toast.error(message);
        return { success: false, message };
      }
    },
    [aToken, backendUrl],
  );

  // Toggle client account status
  const toggleClientStatus = useCallback(
    async (clientId) => {
      try {
        const { data } = await axios.post(
          backendUrl + "/api/admin/clients/toggle-status",
          { clientId },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message);
          // Caller (component) is responsible for refreshing the list
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [aToken, backendUrl],
  );

  const updateVettingStatus = useCallback(
    async (id, status, additionalNote = "") => {
      try {
        const response = await axios.patch(
          `${backendUrl}/api/admin/caregivers/${id}/vetting`,
          { vettingStatus: status, note: additionalNote },
          { headers: { aToken } },
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error updating vetting status",
          error.response?.data || error.message,
        );
        toast.error("Error updating vetting status");
        return { success: false };
      }
    },
    [aToken, backendUrl],
  );

  //updare caregiver cateogry

  const updateCareGiverCategory = useCallback(
    async (caregiverId, category, date_time_preference_to_work) => {
      try {
        const response = await axios.put(
          `${backendUrl}/api/admin/caregivers/${caregiverId}/update-category`,
          { category, date_time_preference_to_work },
          { headers: { aToken } },
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error updating caregiver category and work preference",
          error.response?.data || error.message,
        );
        toast.error("Error updating caregiver category and work preference");
      }
    },
    [aToken, backendUrl],
  );

  const getVerifiedCareGivers = useCallback(
    async ({
      page = 1,
      limit = 5,
      name = "",
      phone = "",
      category = "",
      subcity = "",
      workingStatus = "",
    }) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/verified`,
          {
            params: {
              page,
              limit,
              name,
              phone,
              category,
              subcity,
              workingStatus,
              _t: Date.now(), // Cache buster
            },
            headers: {
              aToken,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          },
        );

        if (data.success) {
          const list = data.caregivers || data.data || [];
          setVerifiedCareGivers(Array.isArray(list) ? list : []);
          setVerifiedCareGiversPagination(
            data.pagination || {
              page: data.page || 1,
              limit: parseInt(limit),
              total: data.total || (Array.isArray(list) ? list.length : 0),
              totalPages: data.pages || 1,
            },
          );
        } else {
          toast.error(data.message || "Failed to fetch verified caregivers");
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    [aToken, backendUrl],
  );

  const getCaregiverWorkingDetails = useCallback(
    async (caregiverId) => {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/caregivers/${caregiverId}/working-details`,
        { headers: { aToken } },
      );
      return data;
    },
    [aToken, backendUrl],
  );

  const getCaregiverWorkHistory = useCallback(
    async (caregiverId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/${caregiverId}/work-history`,
          { headers: { aToken } },
        );
        if (data.success) {
          return data.data;
        } else {
          toast.error(data.message || "Failed to fetch work history");
          return null;
        }
      } catch (error) {
        console.error("Error fetching work history:", error);
        toast.error("Error fetching work history");
        return null;
      }
    },
    [aToken, backendUrl],
  );

  const getCaregiverCVData = useCallback(
    async (caregiverId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/caregivers/cv/${caregiverId}`,
          { headers: { aToken } },
        );

        if (data.success) {
          // Backend returns data in data.data property
          return data.data || data.caregiver;
        } else {
          toast.error(data.message || "Failed to fetch CV data");
          return null;
        }
      } catch (error) {
        console.error("Error fetching caregiver CV:", error);
        toast.error("Error fetching caregiver CV");
        return null;
      }
    },
    [aToken, backendUrl],
  );

  // Toggle caregiver account status (active/inactive) — PUT /:id
  const toggleCareGiverStatus = useCallback(
    async (caregiverId) => {
      try {
        // Fetch current status first, then toggle
        const cgRes = await axios.get(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          { headers: { aToken } },
        );
        const current =
          cgRes.data?.data?.status || cgRes.data?.caregiver?.status;
        const newStatus = current === "active" ? "inactive" : "active";

        const { data } = await axios.put(
          `${backendUrl}/api/admin/caregivers/${caregiverId}`,
          { status: newStatus },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Status updated");
          getAllCareGivers({});
          getVerifiedCareGivers({});
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      }
    },
    [aToken, backendUrl, getAllCareGivers, getVerifiedCareGivers],
  );

  //Requests

  const postJobForClient = useCallback(
    async (jobData) => {
      try {
        const payload = {
          ...jobData,
          serviceCategoryId: jobData.serviceCategoryId || jobData.category,
          location:
            typeof jobData.location === "string"
              ? jobData.location.trim()
              : jobData.location,
        };

        delete payload.category;

        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/post`,
          payload,
          { headers: { aToken } },
        );
        toast.success(data.message);
        return { success: true, job: data.job };
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;

        // Parse specific error types
        if (errorMessage.includes("active appointment")) {
          toast.error(
            "Client has an active appointment in this category. Cannot post job.",
          );
          return {
            success: false,
            type: "appointment-conflict",
            message: errorMessage,
          };
        } else if (errorMessage.includes("active job")) {
          toast.error("Client already has an active job in this category.");
          return { success: false, type: "job-limit", message: errorMessage };
        } else {
          toast.error(errorMessage);
          return { success: false, type: "general", message: errorMessage };
        }
      }
    },
    [aToken, backendUrl],
  );

  const getAllJobs = useCallback(
    async (params = {}) => {
      const {
        page = 1,
        limit = 10,
        search = "",
        status = "All",
        category = "All",
        workTime = "All",
        subcity = "All",
        workState = "All",
      } = params;
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/jobs`, {
          headers: { aToken },
          params: {
            page,
            limit,
            search,
            status,
            category,
            workTime,
            subcity,
            workState,
          },
        });

        if (data.success) {
          setJobs(data.jobs);
          setJobsPagination((prev) => ({
            ...prev,
            page: data.currentPage,
            limit,
            total: data.total,
            totalPages: data.totalPages,
          }));

          return data;
        }
      } catch (error) {
        toast.error("Error fetching jobs");
      }
    },
    [aToken, backendUrl],
  );

  const closeJob = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/close`,
          { jobId },
          { headers: { aToken } },
        );
        toast.success(data.message);
        return { success: true, data };
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;

        if (
          errorMessage.includes("pending") ||
          errorMessage.includes("before assignment")
        ) {
          toast.error(
            "Only pending jobs can be closed. This job has already been assigned.",
          );
        } else {
          toast.error(errorMessage);
        }
        throw new Error(errorMessage);
      }
    },
    [aToken, backendUrl],
  );

  const startJobWork = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/jobs/start-work`,
          { jobId },
          { headers: { aToken } },
        );
        toast.success(data.message || "Work started successfully");
        // Don't auto-refresh here - let the calling component handle it
        return { success: true };
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;
        toast.error(errorMessage);
        return { success: false, message: errorMessage };
      }
    },
    [aToken, backendUrl],
  );

  const completeJob = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/complete`,
          { jobId },
          { headers: { aToken } },
        );
        toast.success(data.message || "Job completed successfully");
        return { success: true };
      } catch (error) {
        const msg = error.response?.data?.message || error.message;
        toast.error(msg);
        return { success: false, message: msg };
      }
    },
    [aToken, backendUrl],
  );

  const cancelJobAdmin = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/cancel`,
          { jobId },
          { headers: { aToken } },
        );
        toast.success(data.message || "Job cancelled successfully");
        return { success: true };
      } catch (error) {
        const msg = error.response?.data?.message || error.message;
        toast.error(msg);
        return { success: false, message: msg };
      }
    },
    [aToken, backendUrl],
  );

  const fetchJobHistory = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/jobs/${jobId}/history`,
          { headers: { aToken } },
        );
        if (data.success) return data.history || [];
        toast.error(data.message || "Failed to fetch job history");
        return [];
      } catch {
        toast.error("Error fetching job history");
        return [];
      }
    },
    [aToken, backendUrl],
  );

  const fetchMatchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/matching/config`,
        { headers: { aToken } },
      );
      if (data.success) return data.data;
      toast.error(data.message || "Failed to load matching configuration");
      return null;
    } catch {
      toast.error("Failed to load matching configuration");
      return null;
    }
  }, [aToken, backendUrl]);

  const saveMatchConfig = useCallback(
    async (payload) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/matching/config`,
          payload,
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Matching configuration saved successfully");
          return { success: true };
        }
        const msg = data.message || "Failed to save matching configuration";
        toast.error(msg);
        return { success: false, message: msg };
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          "Failed to save matching configuration";
        toast.error(msg);
        return { success: false, message: msg };
      }
    },
    [aToken, backendUrl],
  );

  //get specific user data

  const fetchProfile = useCallback(
    async (userId) => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get(
          `${backendUrl}/api/admin/dashboard/get-profile`,
          {
            params: { userId },
            headers: { aToken },
          },
        );

        if (data.success) {
          setProfile(data.userData);
          return data.userData;
        } else {
          setError(data.message);
          toast.error(data.message);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
        toast.error("Error fetching profile data");
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  // Update User Details
  const updateUserDetails = useCallback(
    async (userId, updatedData) => {
      try {
        setLoading(true);
        setError(null);

        const formData = new FormData();
        Object.entries(updatedData).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value);
          }
        });

        const { data } = await axios.post(
          `${backendUrl}/api/admin/clients/${userId}`,
          formData,
          {
            headers: { aToken },
          },
        );

        if (data.success) {
          setProfile((prevProfile) => ({
            ...prevProfile,
            ...data.userData,
          }));
          toast.success(data.message || "User updated successfully!");
          return { success: true };
        } else {
          toast.error(data.message || "Failed to update user.");
          return { success: false, message: data.message };
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        setError(message);
        toast.error("Update failed: " + message);
        return { success: false, message };
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  // Reset User Password (Admin)
  const resetUserPassword = useCallback(
    async (userId) => {
      try {
        setLoading(true);
        setError(null);

        const { data } = await axios.post(
          `${backendUrl}/api/admin/clients/${userId}/reset-password`,
          {},
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message || "Password reset successfully");
          return true;
        } else {
          toast.error(data.message);
          return false;
        }
      } catch (err) {
        console.error(err);
        toast.error("Password reset failed");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  /* ======================================================
      FETCH MANUAL PAYMENTS
       status: all | overdue | paid | in progress | not paid
    ====================================================== */
  const fetchManualPayments = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        const queryParams = new URLSearchParams();
        if (params.status && params.status !== "all") {
          queryParams.append("status", params.status);
        }
        if (params.page) queryParams.append("page", params.page);
        if (params.limit) queryParams.append("limit", params.limit);
        if (params.search) queryParams.append("search", params.search);
        if (params.startDate) queryParams.append("startDate", params.startDate);
        if (params.endDate) queryParams.append("endDate", params.endDate);

        const url = `${backendUrl}/api/admin/manual/all?${queryParams.toString()}`;

        const { data } = await axios.get(url, {
          headers: { aToken },
        });

        if (data.success) {
          setManualPayments(data.payments);
          setManualPaymentsPagination(
            data.pagination || {
              page: params.page || 1,
              limit: params.limit || 5,
              total: data.payments.length,
              totalPages: 1,
            },
          );
        } else {
          toast.error(data.error || "Failed to fetch manual payments");
        }
      } catch (error) {
        console.error("Error fetching manual payments:", error);
        toast.error(
          error.response?.data?.error || "Failed to fetch manual payments",
        );
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  /* ======================================================
        ADD MANUAL PAYMENT (PAY NOW SUPPORTED)
    ====================================================== */
  const addManualPayment = useCallback(
    async (paymentData) => {
      try {
        setLoading(true);

        const { data } = await axios.post(
          `${backendUrl}/api/admin/manual/add`,
          paymentData,
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success("Manual payment added successfully!");
          // Don't fetch here - let the calling component handle navigation/refresh
          return data; // Return data for component to handle
        } else {
          toast.error(data.error || "Failed to add manual payment");
          throw new Error(data.error || "Failed to add manual payment");
        }
      } catch (error) {
        console.error("Error adding manual payment:", error);
        toast.error(
          error.response?.data?.error || "Failed to add manual payment",
        );
        throw error; // Re-throw for component to handle
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  /* ======================================================
        UPDATE MANUAL PAYMENT (PARTIAL / FULL)
    ====================================================== */
  const updateManualPayment = useCallback(
    async (paymentId, updatedData) => {
      if (!paymentId) {
        toast.error("Invalid payment ID");
        throw new Error("Invalid payment ID");
      }

      try {
        setLoading(true);

        const { data } = await axios.put(
          `${backendUrl}/api/admin/manual/update/${paymentId}`,
          updatedData,
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success("Payment updated successfully!");
          return data.payment;
        } else {
          toast.error(data.message || "Failed to update payment");
          throw new Error(data.message || "Failed to update payment");
        }
      } catch (error) {
        console.error("Error updating manual payment:", error);
        toast.error(
          error.response?.data?.message || "Failed to update payment",
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  /* ======================================================
        GET SINGLE MANUAL PAYMENT
    ====================================================== */
  const getManualPaymentById = useCallback(
    async (paymentId) => {
      try {
        setLoading(true);

        const { data } = await axios.get(
          `${backendUrl}/api/admin/manual/${paymentId}`,
          { headers: { aToken } },
        );

        if (data.success) {
          return data.payment;
        } else {
          toast.error(data.message || "Payment not found");
          return null;
        }
      } catch (error) {
        console.error("Error fetching payment:", error);
        toast.error(error.response?.data?.message || "Failed to fetch payment");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  /* ======================================================
        WAIVE PENALTY
    ====================================================== */
  const waivePenalty = useCallback(
    async (paymentId, note) => {
      try {
        setLoading(true);

        const { data } = await axios.patch(
          `${backendUrl}/api/admin/manual/waive-penalty/${paymentId}`,
          { note },
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success("Penalty waived successfully!");
          return data.payment;
        } else {
          toast.error(data.message || "Failed to waive penalty");
          throw new Error(data.message || "Failed to waive penalty");
        }
      } catch (error) {
        console.error("Error waiving penalty:", error);
        toast.error(error.response?.data?.message || "Failed to waive penalty");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  //Assign caregiver to job
  const assignCaregiverToJob = useCallback(
    async (jobId, caregiverId) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/jobs/assign`,
          { jobId, caregiverId },
          { headers: { aToken } },
        );
        toast.success(data.message || "Caregiver assigned successfully");
        // Don't auto-refresh here - let the calling component handle it
        return { success: true, data: data.job };
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;

        // Parse specific error types
        if (
          errorMessage.includes("onboarded") ||
          errorMessage.includes("Onboarded")
        ) {
          toast.error("Only onboarded caregivers can be assigned to jobs.");
          return {
            success: false,
            type: "onboarding-required",
            message: errorMessage,
          };
        } else if (
          errorMessage.includes("currentJob") ||
          errorMessage.includes("active job")
        ) {
          toast.error(
            "Caregiver already has an active job and cannot be assigned.",
          );
          return {
            success: false,
            type: "caregiver-busy",
            message: errorMessage,
          };
        } else if (errorMessage.includes("category")) {
          toast.error("Caregiver category does not match job category.");
          return {
            success: false,
            type: "category-mismatch",
            message: errorMessage,
          };
        } else if (errorMessage.includes("appointment")) {
          toast.error("Client has an active appointment in this category.");
          return {
            success: false,
            type: "client-conflict",
            message: errorMessage,
          };
        } else {
          toast.error(errorMessage);
          return { success: false, type: "general", message: errorMessage };
        }
      }
    },
    [aToken, backendUrl, getAllJobs],
  );

  const getApplicants = useCallback(
    async (jobId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/jobs/${jobId}/applicants`,
          {
            headers: { aToken },
          },
        );

        return data.applicants || [];
      } catch (error) {
        console.error("Failed to get applicants:", error);
        toast.error(
          error.response?.data?.message || "Failed to get applicants",
        );
        return [];
      }
    },
    [aToken, backendUrl],
  );

  const updateApplicationStatus = useCallback(
    async (applicationId, status) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/jobs/update/${applicationId}`,
          { status },
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(`Application ${status} successfully!`);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Error updating application status",
        );
      }
    },
    [aToken, backendUrl],
  );

  // Match caregivers to a job
  const matchCareGiversToJob = useCallback(
    async (jobId) => {
      try {
        setLoading(true);

        const { data } = await axios.get(
          `${backendUrl}/api/admin/matching/match/${jobId}`,
          { headers: { aToken } },
        );

        if (data.success) {
          const matches = data.matchedCaregivers || [];
          setMatchedCareGivers(matches);

          toast.success(`Matched ${matches.length} caregivers successfully`);
          return matches;
        } else {
          toast.error(data.message || "Failed to match caregivers");
          return [];
        }
      } catch (error) {
        console.error("Error matching caregivers:", error);
        toast.error(
          error.response?.data?.message ||
            "Something went wrong while matching caregivers",
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );

  // Fetch matching configuration from the backend
  const loadMatchingConfigFromDB = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/matching/config`,
        {
          headers: { aToken },
        },
      );
      if (data.success) {
        setMatchingConfig(data.data);
        setTempConfig(data.data); // Initialize temporary config
      }
    } catch (error) {
      console.error("Error loading matching configuration:", error);
      toast.error("Failed to load matching configuration");
    }
  }, [aToken, backendUrl]);

  // Save the matching configuration to the database
  const saveMatchingConfigToDB = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/admin/matching/config`,
        tempConfig,
        { headers: { aToken } },
      );
      if (data.success) {
        setMatchingConfig(data.data || tempConfig); // Update the main config state
        toast.success("Matching configuration saved successfully!");
        setIsEditing(false); // Exit edit mode
      }
    } catch (error) {
      console.error("Error saving matching configuration:", error);
      toast.error("Failed to save matching configuration");
    }
  }, [aToken, backendUrl, tempConfig]);

  // Cancel editing and reset to original config
  const cancelEditing = useCallback(() => {
    setTempConfig({ ...matchingConfig });
    setIsEditing(false);
    toast.info("Changes discarded");
  }, [matchingConfig]);

  // Toggle a matching key in the temporary config
  const toggleMatchingKey = useCallback(
    (key) => {
      setTempConfig((prevConfig) => ({
        ...prevConfig,
        [key]: !prevConfig[key],
      }));

      // Display a Toastify message based on the toggle state
      if (!tempConfig[key]) {
        toast.success(`${key} matching is enabled`);
      } else {
        toast.warning(`${key} matching is disabled`);
      }
    },
    [tempConfig],
  );

  // Fetch all trainings
  const fetchAllTrainings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/admin/training/all`, {
        headers: { aToken },
      });
      if (data.success) {
        setTrainings(data.trainings);
      }
    } catch (error) {
      console.error("Error fetching trainings:", error);
      toast.error("Failed to fetch trainings");
    }
  }, [aToken, backendUrl]);

  // Fetch all book purchases
  const fetchAllBookPurchases = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/admin/books/all`, {
        headers: { aToken },
      });
      if (data.success) {
        setBookPurchases(data.bookPurchases);
      }
    } catch (error) {
      console.error("Error fetching book purchases:", error);
      toast.error("Failed to fetch book purchases");
    }
  }, [aToken, backendUrl]);

  // Update training status
  const updateTrainingStatus = useCallback(
    async (trainingId, status) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/training/${trainingId}/status`,
          { status },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(`Training status updated to ${status}`);
          fetchAllTrainings(); // Refresh the list
        }
      } catch (error) {
        console.error("Error updating training status:", error);
        toast.error("Failed to update training status");
      }
    },
    [aToken, backendUrl, fetchAllTrainings],
  );

  // Update book purchase status
  const updateBookPurchaseStatus = useCallback(
    async (bookPurchaseId, status) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/books/${bookPurchaseId}/status`,
          { status },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success(`Book purchase status updated to ${status}`);
          fetchAllBookPurchases(); // Refresh the list
        }
      } catch (error) {
        console.error("Error updating book purchase status:", error);
        toast.error("Failed to update book purchase status");
      }
    },
    [aToken, backendUrl, fetchAllBookPurchases],
  );
  const fetchAllContactForms = useCallback(
    async ({ page = 1, limit = 10, search = "", status = "All" }) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/communication/contact`,
          {
            headers: { aToken },
            params: {
              page,
              limit,
              search,
              status,
            },
          },
        );

        if (data.success) {
          setContactForms(data.data);
          setContactPagination(data.pagination);
        }
      } catch (error) {
        console.error("Error fetching contact forms:", error);
        toast.error("Failed to fetch contact forms");
      }
    },
    [aToken, backendUrl],
  );

  // Fetch all newsletter subscriptions
  const fetchAllNewsletters = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/communication/newsletters`,
        {
          headers: { aToken },
        },
      );
      if (data.success) {
        setNewsletters(data.data);
      }
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      toast.error("Failed to fetch newsletters");
    }
  }, [aToken, backendUrl]);

  // ADMIN SUBSCRIBE
  const subscribeNewsletter = useCallback(
    async (email) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/communication/newsletters/subscribe`,
          { email },
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message);
          // Don't auto-refresh here - let the calling component handle it
        }
      } catch (error) {
        toast.error("Subscription failed");
      }
    },
    [aToken, backendUrl],
  );

  // ADMIN UNSUBSCRIBE
  const unsubscribeNewsletter = useCallback(
    async (id) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/communication/newsletters/unsubscribe/${id}`,
          {},
          { headers: { aToken } },
        );

        if (data.success) {
          toast.success(data.message);
          // Don't auto-refresh here - let the calling component handle it
        }
      } catch (error) {
        toast.error("Unsubscribe failed");
      }
    },
    [aToken, backendUrl],
  );

  // MARK AS READ
  const markContactRead = useCallback(
    async (id) => {
      try {
        await axios.patch(
          `${backendUrl}/api/admin/communication/contact/read/${id}`,
          {},
          { headers: { aToken } },
        );
        // Don't auto-refresh here - let the calling component handle it
      } catch (error) {
        toast.error("Failed to mark as read");
      }
    },
    [aToken, backendUrl],
  );

  // MARK AS REPLIED
  const markContactReplied = useCallback(
    async (ids) => {
      try {
        await axios.patch(
          `${backendUrl}/api/admin/communication/contact/replied`,
          { ids },
          { headers: { aToken } },
        );
        // Don't auto-refresh here - let the calling component handle it
      } catch (error) {
        toast.error("Failed to mark as replied");
      }
    },
    [aToken, backendUrl],
  );
  /* ======================================================
     INVOICE DASHBOARD
  ====================================================== */
  const fetchInvoiceDashboard = useCallback(
    async ({ range = 30, groupBy = "daily" } = {}) => {
      try {
        setLoading(true);

        const { data } = await axios.get(
          `${backendUrl}/api/admin/invoice-dashboard/stats`,
          {
            params: { range, groupBy },
            headers: { aToken },
          },
        );

        if (data.success) {
          return {
            stats: data.stats,
            chartData: data.chartData,
            range: data.range,
            groupBy: data.groupBy,
          };
        }

        toast.error(data.message || "Dashboard failed");
        return null;
      } catch (err) {
        console.error(err);
        toast.error("Failed to load dashboard");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [aToken, backendUrl],
  );
  /* ======================================================
     EXPORT REPORT 
  ====================================================== */
  const exportInvoiceReport = useCallback(
    async (type = "excel", range = 30) => {
      try {
        const now = new Date();
        const yearMonth = `${now.getFullYear()}-${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}`;

        if (type === "excel") {
          const { data } = await axios.get(
            `${backendUrl}/api/admin/invoice-dashboard/export`,
            {
              params: { type, range },
              headers: { aToken },
              responseType: "blob",
            },
          );

          const blob = new Blob([data]);
          const url = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = url;
          link.download = `invoice-report-${yearMonth}.xlsx`;
          link.click();
          URL.revokeObjectURL(url);
          return;
        }

        // ===== PDF =====
        const { data } = await axios.get(
          `${backendUrl}/api/admin/invoice-dashboard/export`,
          {
            params: { type: "pdf", range },
            headers: { aToken },
          },
        );

        generateInvoicePDF(data.report, yearMonth);
      } catch (err) {
        console.error(err);
        toast.error("Export failed");
      }
    },
    [aToken, backendUrl],
  );
  // WorkState helper methods
  const getWorkStateLabel = (workState) => {
    const labels = {
      pending: "Pending",
      assigned: "Assigned",
      "work-started": "Work Started",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return labels[workState] || workState;
  };

  const getWorkStateColor = (workState) => {
    const colors = {
      pending: "bg-yellow-500 text-white",
      assigned: "bg-blue-600 text-white",
      "work-started": "bg-emerald-600 text-white",
      completed: "bg-gray-600 text-white",
      cancelled: "bg-red-600 text-white",
    };
    return colors[workState] || "bg-gray-400 text-white";
  };

  /* ================================================
     PARENT HUB — BLOG & CATEGORY FUNCTIONS
  ================================================ */

  const fetchBlogs = useCallback(
    async ({ page = 1, limit = 10, status, category, search } = {}) => {
      try {
        const params = new URLSearchParams({ page, limit });
        if (status && status !== "All") params.append("status", status);
        if (category) params.append("categoryId", category);
        if (search) params.append("search", search);

        const { data } = await axios.get(
          `${backendUrl}/api/admin/blogs?${params}`,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          setBlogs(data.blogs);
          setBlogPagination(data.pagination);
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch blogs");
      }
    },
    [aToken, backendUrl],
  );

  const fetchBlogById = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/blogs/${blogId}`,
          { headers: { atoken: aToken } },
        );
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch blog");
      }
    },
    [aToken, backendUrl],
  );

  const createBlog = useCallback(
    async (formData) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/blogs`,
          formData,
          {
            headers: {
              atoken: aToken,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        if (data.success) {
          toast.success("Blog created successfully");
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to create blog");
      }
    },
    [aToken, backendUrl],
  );

  const updateBlog = useCallback(
    async (blogId, formData) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/blogs/${blogId}`,
          formData,
          {
            headers: {
              atoken: aToken,
              "Content-Type": "multipart/form-data",
            },
          },
        );
        if (data.success) {
          toast.success("Blog updated successfully");
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update blog");
      }
    },
    [aToken, backendUrl],
  );

  const deleteBlog = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/blogs/${blogId}`,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Blog deleted successfully");
          setBlogs((prev) => prev.filter((b) => b.id !== blogId));
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete blog");
      }
    },
    [aToken, backendUrl],
  );

  const publishBlog = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/blogs/${blogId}/publish`,
          {},
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Blog published");
          setBlogs((prev) =>
            prev.map((b) =>
              b.id === blogId ? { ...b, status: "published" } : b,
            ),
          );
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to publish blog");
      }
    },
    [aToken, backendUrl],
  );

  const unpublishBlog = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/blogs/${blogId}/unpublish`,
          {},
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Blog unpublished");
          setBlogs((prev) =>
            prev.map((b) => (b.id === blogId ? { ...b, status: "draft" } : b)),
          );
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to unpublish blog",
        );
      }
    },
    [aToken, backendUrl],
  );

  const scheduleBlog = useCallback(
    async (blogId, publishAt) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/blogs/${blogId}/schedule`,
          { publishAt },
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Blog scheduled");
          setBlogs((prev) =>
            prev.map((b) =>
              b.id === blogId ? { ...b, status: "scheduled" } : b,
            ),
          );
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to schedule blog");
      }
    },
    [aToken, backendUrl],
  );

  const toggleFeatured = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/blogs/${blogId}/featured`,
          {},
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Blog feature status updated");
          setBlogs((prev) =>
            prev.map((b) =>
              b.id === blogId ? { ...b, isFeatured: data.blog.isFeatured } : b,
            ),
          );
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to toggle featured",
        );
      }
    },
    [aToken, backendUrl],
  );

  const togglePinned = useCallback(
    async (blogId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/blogs/${blogId}/pinned`,
          {},
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success(data.message || "Blog pin status updated");
          setBlogs((prev) =>
            prev.map((b) =>
              b.id === blogId ? { ...b, isPinned: data.blog.isPinned } : b,
            ),
          );
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to toggle pinned");
      }
    },
    [aToken, backendUrl],
  );

  const bulkPublishBlogs = useCallback(
    async (blogIds) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/blogs/bulk/publish`,
          { blogIds },
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success(data.message);
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to bulk publish");
      }
    },
    [aToken, backendUrl],
  );

  const bulkDeleteBlogs = useCallback(
    async (blogIds) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/blogs/bulk/delete`,
          { blogIds },
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success(data.message);
          setBlogs((prev) => prev.filter((b) => !blogIds.includes(b.id)));
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to bulk delete");
      }
    },
    [aToken, backendUrl],
  );

  const fetchBlogAnalytics = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/blogs/analytics/overview`,
        { headers: { atoken: aToken } },
      );
      if (data.success) {
        setBlogAnalytics(data.analytics);
      }
      return data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch analytics");
    }
  }, [aToken, backendUrl]);

  const fetchActivityLogs = useCallback(
    async ({ page = 1, limit = 5, action } = {}) => {
      try {
        const params = new URLSearchParams({ page, limit });
        if (action) params.append("action", action);

        const { data } = await axios.get(
          `${backendUrl}/api/admin/blogs/activity-logs?${params}`,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          setActivityLogs(data.logs);
          setActivityLogsPagination(data.pagination);
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch activity logs",
        );
      }
    },
    [aToken, backendUrl],
  );

  // ── Blog Category Functions ────────────────────────

  const fetchBlogCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/blog-categories`,
        { headers: { atoken: aToken } },
      );
      if (data.success) {
        setBlogCategories(data.categories);
      }
      return data;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch categories",
      );
    }
  }, [aToken, backendUrl]);

  const createBlogCategory = useCallback(
    async (categoryData) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/blog-categories`,
          categoryData,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Category created successfully");
          setBlogCategories((prev) => [...prev, data.category]);
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create category",
        );
      }
    },
    [aToken, backendUrl],
  );

  const updateBlogCategory = useCallback(
    async (categoryId, categoryData) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/admin/blog-categories/${categoryId}`,
          categoryData,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Category updated successfully");
          setBlogCategories((prev) =>
            prev.map((c) => (c.id === categoryId ? data.category : c)),
          );
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to update category",
        );
      }
    },
    [aToken, backendUrl],
  );

  const deleteBlogCategory = useCallback(
    async (categoryId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/blog-categories/${categoryId}`,
          { headers: { atoken: aToken } },
        );
        if (data.success) {
          toast.success("Category deleted successfully");
          setBlogCategories((prev) => prev.filter((c) => c.id !== categoryId));
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to delete category",
        );
      }
    },
    [aToken, backendUrl],
  );

  /* ================================================
     CONTRACTS & PAYOUTS FUNCTIONS
  ================================================ */

  const fetchContracts = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/contracts`, {
          params: filters,
          headers: { aToken },
        });
        if (data.success) {
          const contractsData = (data.data || data.contracts || []).map(
            normalizeContract,
          );
          setContracts(contractsData);

          // Set pagination data from backend response
          setContractsPagination({
            total: data.total || 0,
            totalPages: data.pages || 1,
            currentPage: data.page || 1,
            limit: filters.limit || 5,
          });
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch contracts",
        );
      }
    },
    [aToken, backendUrl],
  );

  const fetchContractById = useCallback(
    async (contractId) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/admin/contracts/${contractId}`,
          { headers: { aToken } },
        );
        const contract = normalizeContract(data.data || data.contract);
        return { ...data, data: contract, contract };
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch contract",
        );
      }
    },
    [aToken, backendUrl],
  );

  const terminateContract = useCallback(
    async (contractId, reason) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/contracts/${contractId}/terminate`,
          { terminationNote: reason, finalStatus: "Cancelled" },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Contract terminated successfully");
          // Don't auto-refresh here - let the calling component handle it
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to terminate contract",
        );
      }
    },
    [aToken, backendUrl],
  );

  const completeContract = useCallback(
    async (contractId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/contracts/${contractId}/terminate`,
          { finalStatus: "Completed" },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Contract completed successfully");
          // Don't auto-refresh here - let the calling component handle it
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to complete contract",
        );
      }
    },
    [aToken, backendUrl],
  );

  const updateContractStartDate = useCallback(
    async (contractId, startDate) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/contracts/${contractId}/start-date`,
          { startDate },
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Contract start date updated successfully");
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "Failed to update contract start date",
        );
        return null;
      }
    },
    [aToken, backendUrl],
  );

  const fetchPayouts = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/payouts`, {
          params: filters,
          headers: { aToken },
        });
        console.log("Payouts API Response:", data);
        if (data.success) {
          console.log("Setting payouts:", data.payouts);
          setPayouts(data.payouts);
        }
        return data;
      } catch (error) {
        console.error("Fetch payouts error:", error);
        toast.error(error.response?.data?.message || "Failed to fetch payouts");
      }
    },
    [aToken, backendUrl],
  );

  const fetchPayoutSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/admin/payouts/summary`,
        { headers: { aToken } },
      );
      return data;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch payout summary",
      );
    }
  }, [aToken, backendUrl]);

  const retryPayout = useCallback(
    async (payoutId) => {
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/admin/payouts/${payoutId}/retry`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Payout retry initiated");
          fetchPayouts();
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to retry payout");
      }
    },
    [aToken, backendUrl, fetchPayouts],
  );

  const fetchWagePayments = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(
          `${backendUrl}/api/payment/admin/all`,
          {
            params: { ...filters, type: "wage" },
            headers: { aToken },
          },
        );
        if (data.success) {
          setWagePayments(data.payments);
        }
        return data;
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch wage payments",
        );
      }
    },
    [aToken, backendUrl],
  );

  /* ================================================
     REVIEWS FUNCTIONS
  ================================================ */

  const fetchReviews = useCallback(
    async (filters = {}) => {
      try {
        const { data } = await axios.get(`${backendUrl}/api/admin/reviews`, {
          params: filters,
          headers: { aToken },
        });
        if (data.success) {
          setReviews(data.data || data.reviews || []);
          setReviewsPagination({
            page: data.pagination?.page || data.page || 1,
            limit: data.pagination?.limit || filters.limit || 10,
            total: data.pagination?.total || data.total || 0,
            totalPages: data.pagination?.totalPages || data.pages || 0,
          });
        }
        return data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch reviews");
      }
    },
    [aToken, backendUrl],
  );

  const hideReview = useCallback(
    async (reviewId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/reviews/${reviewId}/hide`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Review hidden successfully");
          return data;
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to hide review");
        throw error;
      }
    },
    [aToken, backendUrl],
  );

  const unhideReview = useCallback(
    async (reviewId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/reviews/${reviewId}/unhide`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Review unhidden successfully");
          return data;
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to unhide review");
        throw error;
      }
    },
    [aToken, backendUrl],
  );

  const flagReview = useCallback(
    async (reviewId) => {
      try {
        const { data } = await axios.patch(
          `${backendUrl}/api/admin/reviews/${reviewId}/flag`,
          {},
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Review flagged successfully");
          return data;
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to flag review");
        throw error;
      }
    },
    [aToken, backendUrl],
  );

  const deleteReview = useCallback(
    async (reviewId) => {
      try {
        const { data } = await axios.delete(
          `${backendUrl}/api/admin/reviews/${reviewId}`,
          { headers: { aToken } },
        );
        if (data.success) {
          toast.success("Review deleted successfully");
          return data;
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete review");
        throw error;
      }
    },
    [aToken, backendUrl],
  );

  const value = {
    aToken,
    setAToken,
    backendUrl,
    subAdmins,
    adminProfile,
    isSuperAdmin,
    adminAccess,
    adminDepartment,
    hasAdminAccess: (requiredAccess) =>
      canAccessModule(
        {
          ...adminProfile,
          isSuperAdmin,
          adminAccess,
        },
        requiredAccess,
      ),
    fetchSubAdmins,
    fetchAdminProfile,
    createSubAdmin,
    deleteSubAdmin,
    updateAdminProfile,
    updateSubAdminStatus,
    careGivers,
    getAllCareGivers,
    fetchCaregiverById,
    updateCaregiverById,
    createCaregiver,
    updateCaregiverForEdit,
    deleteCareGiverById,
    changeAvailablity,
    changeBookingAvailablity,
    resetCareGiverPassword,
    vettingBoardCaregivers,
    getVettingBoard,
    vettingTableCaregivers,
    vettingPagination,
    getVettingTable,
    bookings,
    bookingsPagination,
    getBookingHistory,
    getAllBookings,
    updateBookingStatus,
    getDashData,
    cancelBooking,
    // legacy exports for backward compatibility during transition
    appointments: bookings,
    appointmentsPagination: bookingsPagination,
    getAppointmentHistory: getBookingHistory,
    getAllAppointments: getAllBookings,
    updateAppointmentStatus: updateBookingStatus,
    cancelAppointment: cancelBooking,
    dashData,
    clients,
    clientsPagination,
    getAllClients,
    createClient,
    toggleClientStatus,
    updateVettingStatus,
    updateCareGiverCategory,
    getVerifiedCareGivers,
    getCaregiverWorkingDetails,
    getCaregiverWorkHistory,
    careGiverPagination,
    getCaregiverVettingHistory,
    verifiedCareGivers,
    verifiedCareGiversPagination,
    toggleCareGiverStatus,
    postJobForClient,
    closeJob,
    assignCaregiverToJob,
    getAllJobs,
    startJobWork,
    completeJob,
    cancelJobAdmin,
    fetchJobHistory,
    fetchMatchConfig,
    saveMatchConfig,
    jobs,
    jobsPagination,
    updateApplicationStatus,
    getApplicants,
    profile,
    loading,
    error,
    fetchProfile,
    updateUserDetails,
    resetUserPassword,
    payments,
    manualPayments,
    manualPaymentsPagination,
    fetchManualPayments,
    addManualPayment,
    updateManualPayment,
    getManualPaymentById,
    waivePenalty,
    matchCareGiversToJob,
    matchedCareGivers,
    loadMatchingConfigFromDB,
    matchingConfig,
    setMatchingConfig,
    tempConfig,
    setTempConfig,
    isEditing,
    setIsEditing,
    toggleMatchingKey,
    saveMatchingConfigToDB,
    cancelEditing,
    trainings,
    bookPurchases,
    fetchAllTrainings,
    fetchAllBookPurchases,
    updateTrainingStatus,
    updateBookPurchaseStatus,
    contactForms,
    contactPagination,
    fetchAllContactForms,
    newsletters,
    fetchAllNewsletters,
    getCaregiversByCategory,
    replaceCaregiver,
    checkClientActiveJobs,
    subscribeNewsletter,
    unsubscribeNewsletter,
    markContactRead,
    markContactReplied,
    coverageData,
    fetchCoverageData,
    fetchInvoiceDashboard,
    exportInvoiceReport,
    getCaregiverCVData,
    getWorkStateLabel,
    getWorkStateColor,
    // Parent Hub (Blog)
    blogs,
    blogPagination,
    blogCategories,
    blogAnalytics,
    activityLogs,
    activityLogsPagination,
    fetchBlogs,
    fetchBlogById,
    createBlog,
    updateBlog,
    deleteBlog,
    publishBlog,
    unpublishBlog,
    scheduleBlog,
    toggleFeatured,
    togglePinned,
    bulkPublishBlogs,
    bulkDeleteBlogs,
    fetchBlogAnalytics,
    fetchActivityLogs,
    fetchBlogCategories,
    createBlogCategory,
    updateBlogCategory,
    deleteBlogCategory,
    // Service Categories (Dynamic EAV)
    serviceCategories,
    fetchServiceCategories,
    fetchServiceCategoryById,
    createServiceCategory,
    updateServiceCategory,
    toggleServiceCategoryActive,
    deleteServiceCategory,
    reorderServiceCategories,
    fetchServiceRequests,
    createServiceRequest,
    fetchCategoryFields,
    // Contracts & Payouts
    contracts,
    payouts,
    wagePayments,
    fetchContracts,
    contractsPagination,
    fetchContractById,
    terminateContract,
    completeContract,
    updateContractStartDate,
    fetchPayouts,
    fetchPayoutSummary,
    retryPayout,
    fetchWagePayments,
    // Reviews
    reviews,
    reviewsPagination,
    fetchReviews,
    hideReview,
    unhideReview,
    flagReview,
    deleteReview,
  };

  return (
    <AdminContext.Provider value={value}>
      {props.children}
    </AdminContext.Provider>
  );
};

export default AdminContextProvider;
